import { useQuery } from '@tanstack/react-query';
import { DOCUMENT_TYPE_META, type DocumentType } from '@makila/fiscal';
import { FilePlus2, FileText } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Badge, Button, Card, Chips, Empty, Input, Loading, PageHeader, Pagination, SearchInput, StatusBadge, Table, Td, Th, Toolbar, Tr } from '../components/ui';
import { get } from '../lib/api';
import { useAuth } from '../lib/auth';
import { date, docLabel, mt, today } from '../lib/format';

interface Row { id: string; doc_type: string; status: string; training: boolean; full_number: string | null; issue_date: string | null; due_date: string | null; gross_total: string; payable_total: string; customer_name: string; customer_nuit: string | null; pending: string | null; converted_to?: string | null; payment_status?: 'paid' | 'partial' | 'open' | null; created_at: string }

const PLURAL: Record<string, string> = {
  FT: 'Facturas', FR: 'Facturas-recibo', VD: 'Vendas a dinheiro', PF: 'Pró-formas', NC: 'Notas de crédito', ND: 'Notas de débito', RE: 'Recibos', GR: 'Guias de remessa', GD: 'Guias de devolução',
};

export function DocumentsPage() {
  const { can } = useAuth();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const type = (sp.get('tipo') ?? '') as DocumentType | '';
  const status = sp.get('estado') ?? '';
  const page = Number(sp.get('page') ?? 1);
  const params = new URLSearchParams({ page: String(page), pageSize: '25' });
  if (type) params.set('type', type);
  if (status) params.set('status', status);
  for (const [k, a] of [['q', 'q'], ['from', 'de'], ['to', 'ate']] as const) { const v = sp.get(a); if (v) params.set(k, v); }
  const q = useQuery({ queryKey: ['documents', params.toString()], queryFn: () => get<{ items: Row[]; total: number }>(`/documents?${params}`) });
  const set = (k: string, v: string) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); n.delete('page'); setSp(n, { replace: true }); };
  const meta = type ? DOCUMENT_TYPE_META[type] : null;
  // NC/ND/GD nascem do documento que corrigem (menu ⋯ da factura): um "novo"
  // solto abria um editor sem origem que depois não podia ser emitido.
  const fromOrigin = !!meta?.requiresOrigin;
  const newType = type && !fromOrigin ? type : 'FT';

  return (
    <>
      <PageHeader eyebrow="Vendas" title={type ? PLURAL[type] : 'Todos os documentos'}
        subtitle={meta ? (meta.fiscal ? 'Documento fiscal — numerado, assinado e imutável depois de emitido.' : 'Sem valor fiscal.') : 'Facturas, recibos, notas, pró-formas e guias'}
        actions={can('documents:create') && !fromOrigin && (
          <Link to={`/documentos/novo?tipo=${newType}`}><Button icon={<FilePlus2 className="size-4" />}>Nova {DOCUMENT_TYPE_META[newType].label.toLowerCase()}</Button></Link>
        )} />
      <Card padded={false}>
        <Toolbar>
          <SearchInput className="w-64" value={sp.get('q') ?? ''} onChange={(v) => set('q', v)} placeholder="Número, cliente ou NUIT" />
          <Chips value={status} onChange={(v) => set('estado', v)} options={[{ id: '', label: 'Todos' }, { id: 'draft', label: 'Rascunhos' }, { id: 'issued', label: 'Emitidos' }, { id: 'voided', label: 'Anulados' }]} />
          <div className="ml-auto flex items-center gap-2">
            <Input type="date" className="h-8 w-36 text-xs" aria-label="Desde" value={sp.get('de') ?? ''} max={today()} onChange={(e) => set('de', e.target.value)} />
            <span className="text-xs text-[var(--muted)]">a</span>
            <Input type="date" className="h-8 w-36 text-xs" aria-label="Até" value={sp.get('ate') ?? ''} onChange={(e) => set('ate', e.target.value)} />
          </div>
        </Toolbar>
        {q.isLoading ? <div className="p-5"><Loading /></div> : !q.data?.items.length ? (
          <Empty icon={<FileText />} title="Nenhum documento" action={can('documents:create') && !fromOrigin && <Link to={`/documentos/novo?tipo=${newType}`}><Button>Criar {DOCUMENT_TYPE_META[newType].label.toLowerCase()}</Button></Link>}>
            {sp.toString() && sp.toString() !== `tipo=${type}` ? 'Ajuste os filtros para ver mais resultados.' : fromOrigin ? `Cria-se a partir do documento a corrigir: abra-o e use o menu ⋯.` : 'Ainda não há documentos deste tipo.'}
          </Empty>
        ) : (
          <>
            <Table>
              <thead><tr><Th>Documento</Th><Th>Cliente</Th><Th>Data</Th><Th>Vencimento</Th><Th>Estado</Th><Th className="text-right">Total</Th><Th className="text-right">Pendente</Th></tr></thead>
              <tbody>
                {q.data.items.map((r) => {
                  const overdue = r.pending && Number(r.pending) > 0 && r.due_date && r.due_date < today();
                  return (
                    <Tr key={r.id} onClick={() => nav(`/documentos/${r.id}`)}>
                      <Td><div className="font-medium">{r.full_number ?? <span className="text-[var(--muted)] italic">Rascunho</span>}</div><div className="text-xs text-[var(--muted)]">{docLabel(r.doc_type)}</div></Td>
                      <Td><div className="max-w-64 truncate">{r.customer_name}</div><div className="text-xs text-[var(--muted)]">{r.customer_nuit ?? ''}</div></Td>
                      <Td>{date(r.issue_date ?? r.created_at)}</Td>
                      <Td className={overdue ? 'font-medium text-red-600' : 'text-[var(--text-2)]'}>{['FT', 'ND', 'PF'].includes(r.doc_type) ? date(r.due_date) : '—'}</Td>
                      <Td><div className="flex flex-wrap items-center gap-1"><StatusBadge status={r.status} training={r.training} />{r.converted_to && r.status === 'issued' ? <span title={`Convertida em ${r.converted_to}`}><Badge tone="blue">Convertida</Badge></span> : null}</div></Td>
                      <Td className="num font-medium">{mt(r.gross_total)}</Td>
                      <Td className="num">{r.pending !== null && r.status === 'issued' ? (Number(r.pending) > 0.004 ? <span className="inline-flex items-center gap-1.5">{r.payment_status === 'partial' ? <Badge tone="amber">Parcial</Badge> : null}<span className={overdue ? 'text-red-600' : ''}>{mt(r.pending)}</span></span> : <span className="text-emerald-700 dark:text-emerald-400">Liquidado</span>) : '—'}</Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
            <Pagination page={page} pageSize={25} total={q.data.total} onPage={(p) => { const n = new URLSearchParams(sp); n.set('page', String(p)); setSp(n); }} />
          </>
        )}
      </Card>
    </>
  );
}
